import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Plus, Home, Shield } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatDate, formatFossaType } from '../utils/formatters';
import CadastroFossaSeptica from './CadastroFossaSeptica';
import { 
  applyIconOnlyStyle,
  getButtonStyle
} from '../utils/buttonStyles';
import { 
  getSelectStyle, 
  labelStyle, 
  formGroupStyle
} from '../utils/formStyles';
import {
  listContainerStyle,
  loadingIndicatorStyle,
  spinnerStyle,
  emptyListStyle,
  emptyListIconStyle,
  pageHeaderStyle,
  obraCardStyle,
  obraCardHeaderStyle,
  obraCardTitleStyle,
  obraCardStatusStyle,
  obraCardBodyStyle,
  obraCardInfoLineStyle,
  obraCardLabelStyle,
  obraCardValueStyle,
  obraCardFooterStyle,
  getFiltersContainerStyle,
  paginationContainerStyle
} from '../utils/listStyles';
import {
  getCardStyle,
  getCardGridStyle,
  cardTitleStyle,
  cardValueStyle,
  cardSubtextStyle,
  getCardTitleColor
} from '../utils/cardStyles';

/**
 * Componente que exibe a lista de fossas sépticas cadastradas
 */
const ListaFossasSepticas = () => {
  const { 
    fossas, 
    residencias,
    carregarFossas, 
    carregarResidencias,
    loading 
  } = useFossasSepticas();
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const residenciaParam = searchParams.get('residencia');

  const [showCadastro, setShowCadastro] = useState(false);
  const [selectedResidencia, setSelectedResidencia] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroResidencia, setFiltroResidencia] = useState(residenciaParam || '');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Carregar dados ao montar o componente
  useEffect(() => {
    carregarFossas();
    carregarResidencias();
  }, [carregarFossas, carregarResidencias]);

  // Abrir formulário de cadastro
  const handleNovaFossa = () => {
    setSelectedResidencia(residenciaParam ? parseInt(residenciaParam) : null);
    setShowCadastro(true);
  };

  // Fechar formulário
  const handleFecharCadastro = () => {
    setShowCadastro(false);
    setSelectedResidencia(null);
  };

  // Após salvar fossa
  const handleSalvoComSucesso = () => {
    setShowCadastro(false);
    setSelectedResidencia(null);
    carregarFossas();
  };

  // Filtrar fossas
  const filteredFossas = fossas.filter(fossa => {
    if (filtroTipo && fossa.tipo !== filtroTipo) return false;
    if (filtroResidencia && fossa.residencia_id !== parseInt(filtroResidencia)) return false;
    return true;
  });

  // Calcular fossas para a página atual
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFossas = filteredFossas.slice(indexOfFirstItem, indexOfLastItem);

  // Mudar de página
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Limpar filtros
  const limparFiltros = () => {
    setFiltroTipo('');
    
    // Se estamos em uma página de residência específica, não limpar o filtro de residência
    if (!residenciaParam) {
      setFiltroResidencia('');
    }
    
    setCurrentPage(1);
    
    // Se estamos em uma página de residência específica e o usuário limpa os filtros,
    // atualizar a URL para remover o parâmetro de residência
    if (residenciaParam) {
      navigate('/fossas-septicas/fossas');
    }
  };

  return (
    <div>
      {showCadastro ? (
        <CadastroFossaSeptica 
          residenciaId={selectedResidencia}
          onClose={handleFecharCadastro}
          onSave={handleSalvoComSucesso}
        />
      ) : (
        <div className="card">
          <div style={pageHeaderStyle}>
            <div>
              {residenciaParam && (
                <div style={{ marginBottom: '10px' }}>
                  <Link 
                    to="/fossas-septicas/fossas" 
                    style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      color: '#2e7d32', 
                      textDecoration: 'none',
                      fontSize: '14px',
                      gap: '5px'
                    }}
                  >
                    <Home size={16} />
                    Voltar para todas as fossas
                  </Link>
                </div>
              )}
              <h2>
                {residenciaParam 
                  ? `Fossas Sépticas da Residência ${residencias.find(r => r.id === parseInt(residenciaParam))?.identificacao || ''}`
                  : 'Fossas Sépticas'
                }
              </h2>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={handleNovaFossa}
                style={getButtonStyle('primary')}
              >
                <Plus size={16} />
                Nova Fossa Séptica
              </button>
            </div>
          </div>

          {/* Cards de Totalizadores */}
          <div style={getCardGridStyle(4)}>
            {/* Card Total */}
            <div style={getCardStyle('neutral')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('neutral') }}>Total de Fossas</h3>
              <div style={cardValueStyle}>
                {filteredFossas.length}
              </div>
              <div style={cardSubtextStyle}>Fossas cadastradas</div>
            </div>
            
            {/* Card Convencionais */}
            <div style={getCardStyle('success')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('success') }}>Fossas Convencionais</h3>
              <div style={cardValueStyle}>
                {filteredFossas.filter(f => f.tipo === 'Convencional').length}
              </div>
              <div style={cardSubtextStyle}>Fossas do tipo convencional</div>
            </div>
            
            {/* Card Biodigestoras */}
            <div style={getCardStyle('info')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('info') }}>Fossas Biodigestoras</h3>
              <div style={cardValueStyle}>
                {filteredFossas.filter(f => f.tipo === 'Biodigestora').length}
              </div>
              <div style={cardSubtextStyle}>Fossas do tipo biodigestora</div>
            </div>
            
            {/* Card Ecológicas */}
            <div style={getCardStyle('info')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('info') }}>Fossas Ecológicas</h3>
              <div style={cardValueStyle}>
                {filteredFossas.filter(f => f.tipo === 'Ecologica').length}
              </div>
              <div style={cardSubtextStyle}>Fossas do tipo ecológica</div>
            </div>
          </div>

          {/* Filtros */}
          <div style={getFiltersContainerStyle(2)}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Tipo de Fossa</label>
              <select 
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                style={getSelectStyle()}
              >
                <option value="">Todos os tipos</option>
                <option value="Convencional">Convencional</option>
                <option value="Biodigestora">Biodigestora</option>
                <option value="Ecologica">Ecológica</option>
                <option value="Sumidouro">Sumidouro</option>
              </select>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Residência</label>
              <select 
                value={filtroResidencia}
                onChange={(e) => {
                  setFiltroResidencia(e.target.value);
                  // Atualizar a URL quando o usuário selecionar uma residência
                  if (e.target.value) {
                    navigate(`/fossas-septicas/fossas?residencia=${e.target.value}`);
                  } else {
                    navigate('/fossas-septicas/fossas');
                  }
                }}
                style={getSelectStyle()}
                disabled={!!residenciaParam} // Desabilitar se estamos em uma página de residência específica
              >
                <option value="">Todas as residências</option>
                {residencias.map(res => (
                  <option key={res.id} value={res.id}>
                    {res.identificacao} - {res.propriedade?.nome || 'Propriedade desconhecida'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button 
              onClick={limparFiltros}
              style={getButtonStyle('secondary')}
            >
              Limpar Filtros
            </button>
          </div>

          {/* Lista de fossas */}
          {loading.fossas ? (
            <div style={loadingIndicatorStyle}>
              <div style={spinnerStyle}></div>
              <p>Carregando fossas sépticas...</p>
            </div>
          ) : (
            <>
              {filteredFossas.length === 0 ? (
                <div style={emptyListStyle}>
                  <Shield size={48} style={emptyListIconStyle} />
                  <p>Nenhuma fossa séptica encontrada com os filtros aplicados.</p>
                  <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button 
                      onClick={limparFiltros}
                      style={getButtonStyle('secondary')}
                    >
                      Limpar Filtros
                    </button>
                    <button 
                      onClick={handleNovaFossa}
                      style={getButtonStyle('primary')}
                    >
                      <Plus size={16} />
                      Cadastrar Nova Fossa Séptica
                    </button>
                  </div>
                </div>
              ) : (
                <div style={listContainerStyle}>
                  {currentFossas.map(fossa => {
                    const residencia = residencias.find(r => r.id === fossa.residencia_id);
                    // Definir cor com base no tipo de fossa
                    let statusColor = '#28a745'; // Verde padrão
                    if (fossa.tipo === 'Biodigestora') statusColor = '#007bff';
                    else if (fossa.tipo === 'Ecologica') statusColor = '#17a2b8';
                    else if (fossa.tipo === 'Sumidouro') statusColor = '#6c757d';
                    
                    return (
                      <div key={fossa.id} style={obraCardStyle}>
                        {/* Cabeçalho do card */}
                        <div style={obraCardHeaderStyle}>
                          <h3 style={obraCardTitleStyle}>{formatFossaType(fossa.tipo)}</h3>
                          <span style={{ 
                            ...obraCardStatusStyle, 
                            backgroundColor: statusColor,
                            color: 'white'
                          }}>
                            {fossa.tipo}
                          </span>
                        </div>
                        
                        {/* Corpo do card */}
                        <div style={obraCardBodyStyle}>
                          <div style={obraCardInfoLineStyle}>
                            <span style={obraCardLabelStyle}>Residência:</span>
                            <span style={obraCardValueStyle}>{residencia?.identificacao || 'Desconhecida'}</span>
                          </div>
                          
                          <div style={obraCardInfoLineStyle}>
                            <span style={obraCardLabelStyle}>Propriedade:</span>
                            <span style={obraCardValueStyle}>{residencia?.propriedade?.nome || 'Desconhecida'}</span>
                          </div>
                          
                          <div style={obraCardInfoLineStyle}>
                            <span style={obraCardLabelStyle}>Data de Instalação:</span>
                            <span style={obraCardValueStyle}>{fossa.data_instalacao ? formatDate(fossa.data_instalacao) : 'Não informada'}</span>
                          </div>
                          
                          <div style={obraCardInfoLineStyle}>
                            <span style={obraCardLabelStyle}>Capacidade:</span>
                            <span style={obraCardValueStyle}>{fossa.capacidade || 'Não informada'}</span>
                          </div>
                        </div>
                        
                        {/* Rodapé do card com botões */}
                        <div style={obraCardFooterStyle}>
                          <Link 
                            to={`/fossas-septicas/mapa?fossa=${fossa.id}`} 
                            style={getButtonStyle('neutral', { small: true })}
                          >
                            <MapPin size={14} />
                            Ver no Mapa
                          </Link>
                          <Link 
                            to={`/fossas-septicas/residencias/${residencia?.propriedade_id}`} 
                            style={getButtonStyle('info', { small: true })}
                          >
                            <Home size={14} />
                            Ver Residências
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Paginação */}
              {filteredFossas.length > itemsPerPage && (
                <div style={paginationContainerStyle}>
                  {Array.from({ length: Math.ceil(filteredFossas.length / itemsPerPage) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => paginate(index + 1)}
                      style={{
                        ...applyIconOnlyStyle(
                          getButtonStyle(currentPage === index + 1 ? 'primary' : 'secondary')
                        ),
                        width: '32px',
                        height: '32px',
                        margin: '0 5px',
                        padding: '0',
                        justifyContent: 'center'
                      }}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .propriedades-grid {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .propriedade-card {
          background-color: white;
          border-radius: 8px;
          padding: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ListaFossasSepticas;